import { Link, usePage } from '@inertiajs/react';
import { LayoutDashboard, LogOut, User as UserIcon } from 'lucide-react';
import { BottomNav, tabs } from '@/components/bottom-nav';
import { PageTransition } from '@/components/page-transition';
import { logout } from '@/routes';
import type { User } from '@/types';

export default function SantriLayout({
    children,
}: {
    children: React.ReactNode;
}) {
    const { props, url } = usePage<{ auth: { user: User } }>();
    const user = props.auth.user;

    const hour = parseInt(
        new Intl.DateTimeFormat('id-ID', {
            timeZone: 'Asia/Jakarta',
            hour: 'numeric',
            hour12: false,
        }).format(new Date()),
        10,
    );
    const greeting =
        hour < 11
            ? 'Selamat Pagi'
            : hour < 15
              ? 'Selamat Siang'
              : hour < 18
                ? 'Selamat Sore'
                : 'Selamat Malam';

    function isActive(href: string): boolean {
        if (href === '/dashboard') {
            return url === '/dashboard';
        }

        if (href === '/presensi') {
            return (
                url.startsWith('/presensi') &&
                !url.startsWith('/presensi/rekam')
            );
        }

        return url.startsWith(href);
    }

    return (
        <div className="min-h-screen bg-[#d9ffee] font-sans text-[#003628] lg:flex">
            {/* Desktop Sidebar (hidden on mobile/tablet) */}
            <aside className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col lg:bg-white lg:shadow-[24px_0_40px_rgba(0,54,40,0.04)]">
                {/* Brand / Logo Area */}
                <div className="flex flex-col border-b border-[#d9ffee]/50 p-6">
                    <div className="flex flex-col text-center">
                        <span className="text-xl font-black tracking-tighter text-[#006944] uppercase">
                            <span>My ASWAJA</span>
                        </span>
                    </div>

                    {user.role === 'admin' && (
                        <Link
                            href="/admin/dashboard"
                            className="group mt-6 flex items-center gap-3 rounded-[1.25rem] bg-[#ffd709]/10 p-3 ring-1 ring-[#ffd709]/30 transition-all hover:bg-[#ffd709]/20 active:scale-95"
                        >
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#ffd709] text-[#5b4b00] shadow-sm group-hover:shadow-[#ffd709]/40">
                                <LayoutDashboard className="h-5 w-5" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs font-black tracking-wider text-[#6c5a00] uppercase">
                                    <span>Panel Admin</span>
                                </span>
                                <span className="text-[10px] font-bold text-[#006944]">
                                    <span>Kelola Sistem →</span>
                                </span>
                            </div>
                        </Link>
                    )}
                </div>

                {/* Sidebar Navigation */}
                <div className="flex-1 space-y-2 overflow-y-auto px-4 py-8">
                    {tabs.map((tab) => {
                        const active = isActive(tab.href);
                        const Icon = tab.icon;

                        return (
                            <Link
                                key={tab.key}
                                href={tab.href}
                                className={`flex items-center gap-4 rounded-2xl px-5 py-4 font-bold transition-all ${
                                    active
                                        ? 'bg-[#006944] text-[#ffd709] shadow-lg shadow-[#006944]/20'
                                        : 'text-[#306554] hover:bg-[#d9ffee]/50 hover:text-[#003628]'
                                }`}
                            >
                                <Icon
                                    className="h-6 w-6"
                                    strokeWidth={active ? 2.5 : 2}
                                />
                                <span>{tab.name}</span>
                            </Link>
                        );
                    })}
                </div>

                {/* User Info & Logout */}
                <div className="cursor-default p-6">
                    <div className="flex items-center gap-4 rounded-3xl bg-[#d9ffee] p-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 border-[#6decae] bg-[#006944] text-lg font-bold text-white shadow-sm">
                            <span>{user.name?.charAt(0).toUpperCase()}</span>
                        </div>
                        <div className="flex flex-1 flex-col overflow-hidden">
                            <span className="truncate text-sm font-bold text-[#003628]">
                                <span>{user.name}</span>
                            </span>
                            <span className="text-[10px] font-bold tracking-widest text-[#6c5a00] uppercase">
                                <span>
                                    {user.role === 'admin'
                                        ? 'Santri (Admin)'
                                        : 'Santri'}
                                </span>
                            </span>
                        </div>
                        <Link
                            href={logout()}
                            method="post"
                            as="button"
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#b31b25] shadow-sm transition-all hover:bg-[#fb5151] hover:text-white active:scale-95"
                            title="Log Out"
                        >
                            <LogOut className="h-5 w-5" />
                        </Link>
                    </div>
                </div>
            </aside>

            {/* Main Wrapper (Pushed to right on desktop) */}
            <div className="flex min-h-screen flex-1 flex-col lg:pl-72">
                {/* Mobile Top Header (hidden on desktop) */}
                <header className="sticky top-0 z-40 w-full lg:hidden">
                    <div className="flex h-16 w-full items-center justify-between border-b border-[#006944]/10 bg-white/95 px-6 transition-all supports-[backdrop-filter]:bg-white/80 supports-[backdrop-filter]:backdrop-blur-xl">
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <div className="h-11 w-11 overflow-hidden rounded-full border-2 border-[#6decae] shadow-sm">
                                    <div className="flex h-full w-full items-center justify-center bg-[#006944] text-base font-black text-white">
                                        <span>
                                            {user.name?.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                </div>
                                <div className="absolute -right-0.5 -bottom-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-[#ffd709]" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] font-black tracking-[0.15em] text-[#6c5a00] uppercase">
                                    <span>{greeting}</span>
                                </span>
                                <span className="text-lg leading-tight font-black tracking-tight text-[#003628]">
                                    <span>{user.name?.split(' ')[0]}</span>
                                </span>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            {user.role === 'admin' && (
                                <Link
                                    href="/admin/dashboard"
                                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#ffd709]/10 text-[#6c5a00] transition-all active:scale-90"
                                >
                                    <LayoutDashboard className="h-5 w-5" />
                                </Link>
                            )}
                            <Link
                                href="/profil"
                                className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#006944]/5 text-[#006944] transition-all active:scale-90"
                            >
                                <UserIcon className="h-5 w-5" />
                            </Link>
                        </div>
                    </div>
                </header>

                {/* Main Content Area */}
                <main className="mx-auto w-full max-w-5xl flex-1 px-4 pt-16 pb-32 lg:px-12 lg:pt-12">
                    <PageTransition>{children}</PageTransition>
                </main>

                {/* Mobile Bottom Navigation (hidden on desktop) */}
                <BottomNav />
            </div>
        </div>
    );
}
